import { api } from "../api";
import { ddmmaaaa } from "../../lib/fecha";
import { formatearPesos } from "../../lib/money";
import { iconoImpresora } from "../iconos";
import logoUrl from "../../img/logo-torremolinos.jpeg";
import { agruparProduccion, matrizProduccion } from "../../lib/produccion";
import type {
  ProduccionRespuesta,
  ProduccionItem,
  ProduccionDetalle,
  PedidoResumen,
} from "../../lib/types";

interface PedidoDetalleResp {
  pedido: {
    id: number;
    fecha_entrega: string;
    total: number;
    descuento: number;
    cliente_nombre: string;

    cliente_direccion: string | null;
    empresa_nombre: string | null;
    empresa_direccion: string | null;
    empresa_telefono: string | null;
  };
  lineas: Array<{
    producto_id: number;
    cantidad: number;
    precio_unitario: number;
    subtotal: number;
    codigo: number;
    descripcion: string;
    empaque: number;
  }>;
}

/**
 * Imprime SOLO el html dado (remito, hoja de produccion) en un iframe aparte.
 *
 * Antes esto se hacia volcando el html en un #print-target y tapando el resto
 * de la pagina con una clase, que se sacaba en el evento `afterprint`. En
 * Android eso sale mal: `print()` no bloquea, `afterprint` dispara al toque y
 * la clase se saca ANTES de que el sistema arme la hoja, asi que se imprime la
 * pagina entera. Con un iframe el documento a imprimir es otro documento: no
 * depende de ningun evento ni de tapar nada, y sale igual en telefono y en PC.
 */
function imprimirElemento(html: string): void {
  // Los estilos de la pagina (Tailwind ya compilado) tienen que viajar al
  // iframe: es un documento nuevo y no hereda nada.
  const estilos = Array.from(
    document.querySelectorAll('link[rel="stylesheet"], style'),
  )
    .map((n) => n.outerHTML)
    .join("");

  document.getElementById("iframe-impresion")?.remove();
  const marco = document.createElement("iframe");
  marco.id = "iframe-impresion";
  marco.setAttribute("aria-hidden", "true");
  // Fuera de la vista pero con tamano real: con 0x0 algunos navegadores no
  // maquetan el contenido y sale una hoja en blanco.
  marco.style.cssText =
    "position:fixed;right:0;bottom:0;width:210mm;height:297mm;border:0;opacity:0;pointer-events:none;";
  marco.srcdoc = `<!doctype html><html lang="es"><head><meta charset="utf-8">${estilos}</head><body class="bg-white text-black">${html}</body></html>`;

  marco.addEventListener("load", () => {
    const win = marco.contentWindow;
    if (!win) return;
    // Esperar las imagenes (el logo) o la hoja sale sin logo.
    const imgs = Array.from(win.document.images);
    const listas = imgs.map((im) =>
      im.complete
        ? Promise.resolve()
        : new Promise<void>((ok) => {
            im.addEventListener("load", () => ok());
            im.addEventListener("error", () => ok());
          }),
    );
    void Promise.all(listas).then(() => {
      win.focus();
      win.print();
      // No se puede confiar en `afterprint` para limpiar (ver arriba): se saca
      // el iframe bastante despues, cuando el sistema ya tomo el documento.
      setTimeout(() => marco.remove(), 60000);
    });
  });

  document.body.appendChild(marco);
}

// Los pedidos admiten fracciones (2,5 kilos): los numeros se muestran con coma
// decimal y sin ceros de relleno.
const nf = new Intl.NumberFormat("es-UY", { maximumFractionDigits: 2 });
const num = (n: number): string => nf.format(n);

function esc(s: unknown): string {
  return String(s ?? "").replace(/[&<>"]/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] as string,
  );
}


export async function renderProduccion(contenedor: HTMLElement): Promise<void> {
  contenedor.innerHTML = `
    <div class="mb-4 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 id="titulo" class="titulo-pagina">Produccion</h1>
        <p class="subtitulo">Pedidos del dia y total a producir por area.</p>
      </div>
      <div class="no-print flex items-end gap-2">
        <label><span class="etiqueta">Fecha</span><input id="fecha" type="date" class="campo" /></label>
        <button id="imprimir" class="btn-sec">${iconoImpresora} Imprimir</button>
      </div>
    </div>
    <div id="salida" class="space-y-4"></div>
    <dialog id="dlgPedido"></dialog>`;

  const inpFecha = contenedor.querySelector("#fecha") as HTMLInputElement;
  const salida = contenedor.querySelector("#salida") as HTMLElement;
  const titulo = contenedor.querySelector("#titulo") as HTMLElement;
  const dlgPedido = contenedor.querySelector("#dlgPedido") as HTMLDialogElement;
  let ultima: ProduccionRespuesta | null = null;
  // El desglose por envase de los grupos arranca plegado: a la cuadra le
  // alcanza el total por articulo (lo demas los confunde y lo terminan
  // marcando con fluo). El chip "Ver desglose" lo abre cuando hace falta y la
  // impresion sale como este la pantalla.
  let desglose = false;

  async function cargar(): Promise<void> {
    const q = inpFecha.value ? `?fecha=${inpFecha.value}` : "";
    salida.innerHTML = `<div class="tarjeta text-masa-400">Cargando...</div>`;
    try {
      const r = await api.get<ProduccionRespuesta>(`/api/produccion${q}`);
      inpFecha.value = r.fecha;
      titulo.textContent = `Produccion del ${ddmmaaaa(r.fecha)}`;
      pintar(r);
    } catch (e) {
      salida.innerHTML = `<div class="tarjeta text-red-700">${esc(e instanceof Error ? e.message : "Error")}</div>`;
    }
  }

  function pintar(r: ProduccionRespuesta): void {
    if (r.items.length === 0) {
      salida.innerHTML = `<div class="tarjeta text-center text-masa-400">Sin produccion para ${esc(ddmmaaaa(r.fecha))}.</div>`;
      return;
    }

    ultima = r;
    salida.innerHTML =
      resumen(r.pedidos) + cardMatriz(r.items, r.detalle) + cardProduccion(r.items, r.fecha);
  }

  // Tablas limpias por area: Articulo | Paquetes | Unidades (lo que hornea la
  // cuadra, sin el desglose por cliente). Los articulos que comparten grupo de
  // produccion salen en UNA fila con el total en unidades y, si el desglose
  // esta abierto, abajo en chico de que envases salio. Compartido por pantalla
  // e impresion; la hoja del horno va SIN la columna de paquetes (a la cuadra
  // el "29 x 2" la confunde: alcanza el total en unidades).
  function tablasProduccion(
    items: ProduccionItem[],
    opciones: { conDesglose: boolean; conPaquetes: boolean },
  ): string {
    const bloques: string[] = [];
    for (const { area, filas } of agruparProduccion(items)) {
      const cuerpo = filas
        .map((f) => {
          // Del horno no sale media unidad: se redondea para arriba y se avisa.
          const redondeo =
            f.unidades !== f.unidadesExactas
              ? `<div class="text-[11px] font-normal text-masa-500">redondeado para arriba (exacto: ${num(f.unidadesExactas)})</div>`
              : "";
          // En una fila sin grupo la cuenta va en la misma linea ("7 x 3"): no
          // hay nada que desglosar, pero igual se ve de donde salen las unidades.
          const paquetes =
            !f.esGrupo && f.partes[0] && f.partes[0].empaque > 1
              ? `${num(f.bultos)} x ${f.partes[0].empaque}`
              : num(f.bultos);
          const cabecera = `<tr class="border-t border-black/10">
            <td class="px-2 pb-0.5 pt-1.5">
              <span class="${f.esGrupo ? "font-bold uppercase" : ""}">${esc(f.etiqueta)}</span>
              ${redondeo}
            </td>
            ${opciones.conPaquetes ? `<td class="px-2 pb-0.5 pt-1.5 text-right tabular-nums text-masa-500">${paquetes}</td>` : ""}
            <td class="px-2 pb-0.5 pt-1.5 text-right font-bold tabular-nums">${num(f.unidades)}</td>
          </tr>`;
          // El desglose del grupo va UNA LINEA POR ENVASE, con sus propias
          // columnas, para que se lea "13 x 1 = 13" de un golpe. Se muestra
          // aunque el grupo tenga un solo envase: abierto, la hoja dice siempre
          // de que articulo salio el total.
          if (!f.esGrupo || !opciones.conDesglose) return cabecera;
          const partes = f.partes
            .map(
              (pt) => `<tr class="text-masa-500">
                <td class="py-0.5 pl-5 pr-2 text-[13px]">${esc(pt.descripcion)}</td>
                ${opciones.conPaquetes ? `<td class="px-2 py-0.5 text-right text-[13px] tabular-nums">${num(pt.bultos)} x ${pt.empaque}</td>` : ""}
                <td class="px-2 py-0.5 text-right text-[13px] tabular-nums">${num(pt.unidades)}</td>
              </tr>`,
            )
            .join("");
          return cabecera + partes;
        })
        .join("");
      bloques.push(`
        <div class="mb-3 break-inside-avoid">
          <h3 class="mb-1 border-b-2 border-masa-600 text-sm font-bold uppercase tracking-wide text-masa-700">${esc(area)}</h3>
          <table class="w-full text-sm">
            <thead class="text-[11px] uppercase tracking-wider text-masa-400"><tr>
              <th class="px-2 text-left font-normal">Articulo</th>
              ${opciones.conPaquetes ? `<th class="px-2 text-right font-normal">Paquetes</th>` : ""}
              <th class="px-2 text-right font-normal">Unidades</th>
            </tr></thead>
            <tbody>${cuerpo}</tbody>
          </table>
        </div>`);
    }
    return bloques.join("");
  }

  // Matriz articulo x cliente en unidades, con la columna de totales al final
  // (la planilla que la panaderia llevaba en Excel).
  function tablaMatriz(items: ProduccionItem[], detalle: ProduccionDetalle[]): string {
    const m = matrizProduccion(items, detalle);
    if (m.columnas.length === 0 || m.filas.length === 0) return "";
    const encabezado = m.columnas
      .map((c) => `<th class="px-2 py-1 text-right font-semibold">${esc(c.cliente)}</th>`)
      .join("");
    const cuerpo = m.filas
      .map((f) => {
        const madre = `<tr class="border-t border-black/10">
          <td class="whitespace-nowrap px-2 py-1.5 ${f.esGrupo ? "font-bold uppercase" : "font-medium"}">${esc(f.etiqueta)}</td>
          ${f.celdas
            .map(
              (n) =>
                `<td class="px-2 py-1.5 text-right tabular-nums ${n ? "" : "text-masa-300"}">${n ? num(n) : "—"}</td>`,
            )
            .join("")}
          <td class="px-2 py-1.5 text-right font-bold tabular-nums">${num(f.total)}</td>
        </tr>`;
        if (!f.esGrupo) return madre;
        // El desglose del grupo, como en la hoja del horno: una linea por
        // envase con su reparto por cliente. Va anidado en la fila madre, asi
        // que el TOTAL del pie (que suma solo filas madre) no lo cuenta dos veces.
        const partes = f.partes
          .map(
            (pt) => `<tr class="text-masa-500">
              <td class="whitespace-nowrap py-0.5 pl-5 pr-2 text-[13px]">${esc(pt.etiqueta)}</td>
              ${pt.celdas
                .map(
                  (n) =>
                    `<td class="px-2 py-0.5 text-right text-[13px] tabular-nums ${n ? "" : "text-masa-300"}">${n ? num(n) : "—"}</td>`,
                )
                .join("")}
              <td class="px-2 py-0.5 text-right text-[13px] tabular-nums">${num(pt.total)}</td>
            </tr>`,
          )
          .join("");
        return madre + partes;
      })
      .join("");
    const totalesCol = m.columnas.map((_, i) =>
      m.filas.reduce((a, f) => a + f.celdas[i], 0),
    );
    return `
      <table class="w-full min-w-max text-sm">
        <thead class="text-[11px] uppercase tracking-wider text-masa-400"><tr>
          <th class="px-2 py-1 text-left font-semibold">Articulo</th>
          ${encabezado}
          <th class="px-2 py-1 text-right font-semibold">Totales</th>
        </tr></thead>
        <tbody>${cuerpo}</tbody>
        <tfoot><tr class="border-t-2 border-masa-300 font-bold">
          <td class="px-2 py-1.5">TOTAL</td>
          ${totalesCol.map((n) => `<td class="px-2 py-1.5 text-right tabular-nums">${num(n)}</td>`).join("")}
          <td class="px-2 py-1.5 text-right tabular-nums">${num(totalesCol.reduce((a, b) => a + b, 0))}</td>
        </tr></tfoot>
      </table>`;
  }

  function cardMatriz(items: ProduccionItem[], detalle: ProduccionDetalle[]): string {
    const tabla = tablaMatriz(items, detalle);
    if (!tabla) return "";
    return `
      <div class="tarjeta">
        <h2 class="mb-1 text-sm font-bold uppercase tracking-wide text-masa-600">Detalle por cliente</h2>
        <p class="mb-3 text-sm text-masa-400">
          En unidades, ya multiplicado por el paquete de cada articulo. Aca no se redondea:
          es lo que lleva cada cliente, tal cual se le facturo.
        </p>
        <div class="overflow-x-auto">${tabla}</div>
      </div>`;
  }

  // Tarjeta grande "PRODUCCION" con su propio boton de imprimir (solo lo que hay
  // que producir). El boton de arriba imprime TODA la pagina.
  function cardProduccion(items: ProduccionItem[], fecha: string): string {
    return `
      <div class="tarjeta">
        <div class="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 class="text-3xl font-extrabold uppercase tracking-tight text-masa-800">
            Produccion <span class="text-masa-500">— ${esc(ddmmaaaa(fecha))}</span>
          </h2>
          <div class="no-print flex flex-wrap items-center gap-2">
            <button id="toggle-desglose" class="btn-sec">${desglose ? "Ocultar desglose" : "Ver desglose"}</button>
            <button id="imprimir-prod" class="btn-primario">${iconoImpresora} Imprimir produccion</button>
          </div>
        </div>
        <div class="columns-1 sm:columns-2" style="column-gap:1.5rem">${tablasProduccion(items, { conDesglose: desglose, conPaquetes: true })}</div>
      </div>`;
  }

  function resumen(pedidos: PedidoResumen[]): string {
    const totalDia = pedidos.reduce((a, p) => a + p.total, 0);
    const filas = pedidos
      .map(
        (p) => `<tr class="cursor-pointer border-t border-crema-100 hover:bg-crema-50" data-ver-pedido="${p.id}" title="Ver detalle">
          <td class="py-1 pr-2 font-medium text-masa-700">${esc(p.cliente)}</td>
          <td class="py-1 pr-2 text-masa-400">#${p.id}</td>
          <td class="py-1 text-right tabular-nums">${formatearPesos(p.total)}</td>
        </tr>`,
      )
      .join("");
    return `
      <div class="tarjeta">
        <h2 class="mb-2 text-sm font-bold uppercase tracking-wide text-masa-600">Resumen de pedidos del dia <span class="font-normal normal-case text-masa-400">(toca un cliente para ver e imprimir su remito)</span></h2>
        <table class="w-full text-sm">
          <thead class="text-[11px] uppercase tracking-wider text-masa-400"><tr>
            <th class="text-left font-normal">Cliente</th><th class="text-left font-normal">Pedido</th><th class="text-right font-normal">Total</th>
          </tr></thead>
          <tbody>${filas}</tbody>
          <tfoot><tr class="border-t-2 border-masa-300 font-bold">
            <td class="py-2" colspan="2">TOTAL DEL DIA</td>
            <td class="py-2 text-right tabular-nums">${formatearPesos(totalDia)}</td>
          </tr></tfoot>
        </table>
      </div>`;
  }

  // Un remito (una via), identico al que sale al confirmar el pedido.
  // Se muestran/imprimen dos: Via 1 (cliente) y Via 2 (control).
  function remitoVia(
    p: PedidoDetalleResp["pedido"],
    lineas: PedidoDetalleResp["lineas"],
    via: number,
  ): string {
    const filas = lineas
      .map(
        (l) => `<tr class="border-t border-black/20">
          <td class="py-1 pr-2">${esc(l.descripcion)}</td>
          <td class="py-1 pr-2 text-right">${l.cantidad}</td>
          <td class="py-1 pr-2 text-right">${formatearPesos(l.precio_unitario)}</td>
          <td class="py-1 text-right">${formatearPesos(l.subtotal)}</td>
        </tr>`,
      )
      .join("");
    const emite = p.empresa_nombre
      ? `${esc(p.empresa_nombre)}${p.empresa_direccion ? ` · ${esc(p.empresa_direccion)}` : ""}${p.empresa_telefono ? ` · Tel ${esc(p.empresa_telefono)}` : ""}`
      : "";
    return `
      <div class="mx-auto max-w-lg bg-white p-4 text-black ${via === 1 ? "print:break-after-page" : ""}">
        <div class="flex items-start justify-between">
          <div class="mb-2 flex items-center gap-3">
            <img src="${logoUrl}" alt="Torremolinos" class="h-16 w-auto" />
            <h2 class="text-xl font-bold">Remito</h2>
          </div>
          <span class="rounded border border-black/30 px-2 py-0.5 text-xs font-bold">VIA ${via} — ${via === 1 ? "CLIENTE" : "CONTROL"}</span>
        </div>
        ${emite ? `<div class="border-b border-black/20 pb-1 text-xs"><b>Emite:</b> ${emite}</div>` : ""}
        <div class="mt-1 text-sm">
          <div><b>Cliente:</b> ${esc(p.cliente_nombre)}</div>
          ${p.cliente_direccion ? `<div><b>Direccion:</b> ${esc(p.cliente_direccion)}</div>` : ""}
          <div><b>Entrega:</b> ${ddmmaaaa(p.fecha_entrega)}</div>
          <div><b>Remito:</b> #${p.id}</div>
        </div>
        <table class="mt-3 w-full text-sm">
          <thead><tr class="text-left text-xs">
            <th>Producto</th><th class="text-right">Cant.</th><th class="text-right">P.Unit</th><th class="text-right">Subtotal</th>
          </tr></thead>
          <tbody>${filas}</tbody>
        </table>
        ${p.descuento ? `<div class="mt-2 text-right text-sm">Descuento: ${formatearPesos(p.descuento)}</div>` : ""}
        <div class="mt-3 text-right text-lg font-bold">Total: ${formatearPesos(p.total)}</div>
        <div class="mt-6 flex justify-between text-xs text-black/60">
          <span>Firma: ____________________</span>
          <span>Aclaracion: ____________________</span>
        </div>
      </div>`;
  }

  async function verPedido(id: number): Promise<void> {
    dlgPedido.innerHTML = `<div class="p-6 text-masa-400">Cargando remito...</div>`;
    dlgPedido.showModal();
    try {
      const r = await api.get<PedidoDetalleResp>(`/api/pedidos/${id}`);
      const via1 = remitoVia(r.pedido, r.lineas, 1);
      const via2 = remitoVia(r.pedido, r.lineas, 2);
      const paraImprimir = via1 + via2;
      dlgPedido.innerHTML = `
        <div class="w-[34rem] max-w-full">
          ${via1}
          <div class="my-3 border-t-2 border-dashed border-crema-300"></div>
          ${via2}
          <div class="no-print flex justify-end gap-2 border-t border-crema-200 p-3">
            <button id="p_cerrar" class="btn-sec">Cerrar</button>
            <button id="p_imprimir" class="btn-primario">${iconoImpresora} Imprimir remito (2 vias)</button>
          </div>
        </div>`;
      dlgPedido.querySelector("#p_cerrar")?.addEventListener("click", () => dlgPedido.close());
      dlgPedido.querySelector("#p_imprimir")?.addEventListener("click", () => imprimirElemento(paraImprimir));
    } catch (e) {
      dlgPedido.innerHTML = `<div class="p-6"><p class="text-red-700">${esc(e instanceof Error ? e.message : "Error")}</p><div class="mt-3 text-right"><button id="p_cerrar" class="btn-sec">Cerrar</button></div></div>`;
      dlgPedido.querySelector("#p_cerrar")?.addEventListener("click", () => dlgPedido.close());
    }
  }

  salida.addEventListener("click", (ev) => {
    const target = ev.target as HTMLElement;
    if (target.closest("#toggle-desglose") && ultima) {
      desglose = !desglose;
      pintar(ultima);
      return;
    }
    if (target.closest("#imprimir-prod") && ultima) {
      // Solo la hoja del horno: el detalle por cliente NO va aca (es para
      // mirar en pantalla o para el boton Imprimir de arriba, que saca la
      // pagina entera). El desglose sale como este en pantalla, pero SIN la
      // columna de paquetes: la cuadra pidio solo los totales en unidades.
      const html = `
        <div class="mx-auto max-w-2xl p-4 text-black">
          <h1 class="text-2xl font-extrabold uppercase">Produccion</h1>
          <p class="mb-3 text-sm">Entrega del ${ddmmaaaa(ultima.fecha)}</p>
          <div class="columns-1 sm:columns-2" style="column-gap:1.5rem">${tablasProduccion(ultima.items, { conDesglose: desglose, conPaquetes: false })}</div>
        </div>`;
      imprimirElemento(html);
      return;
    }
    const row = target.closest("[data-ver-pedido]");
    if (row) void verPedido(Number(row.getAttribute("data-ver-pedido")));
  });

  // Cambiar la fecha carga sola: no hay boton "Ver". Asignar inpFecha.value
  // desde cargar() no dispara 'change', asi que no hay bucle.
  inpFecha.addEventListener("change", () => void cargar());
  contenedor.querySelector("#imprimir")?.addEventListener("click", () => window.print());
  await cargar();
}
